import { useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import AppButton from "../../components/AppButton";
import { SERIES_COLORS } from "./constants";
import { fetchActivation, setActivation } from "./services";

const Dot = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: 9,
      height: 9,
      borderRadius: "50%",
      backgroundColor: color,
      ml: 1,
    }}
  />
);

/** Which forecast the batch writes, and the confirmation that changes it. */
const ForecastActivation = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery("ml-forecast-activation", fetchActivation, {
    refetchOnWindowFocus: false,
  });
  const mutation = useMutation(setActivation, {
    onSuccess: () => {
      queryClient.invalidateQueries("ml-forecast-activation");
      setOpen(false);
    },
  });

  if (data === undefined) return null;

  const { active } = data;
  const color = active ? SERIES_COLORS.ml : SERIES_COLORS.baseline;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          size="small"
          variant="outlined"
          icon={<Dot color={color} />}
          label={intl.formatMessage({
            id: active
              ? "ML_FORECAST_ACTIVATION_ON"
              : "ML_FORECAST_ACTIVATION_OFF",
          })}
        />
        <AppButton intent="tonal" size="small" onClick={() => setOpen(true)}>
          {intl.formatMessage({
            id: active
              ? "ML_FORECAST_ACTIVATION_OFF_CONFIRM"
              : "ML_FORECAST_ACTIVATION_ON_CONFIRM",
          })}
        </AppButton>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {intl.formatMessage({
            id: active
              ? "ML_FORECAST_ACTIVATION_OFF_TITLE"
              : "ML_FORECAST_ACTIVATION_ON_TITLE",
          })}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {intl.formatMessage({
              id: active
                ? "ML_FORECAST_ACTIVATION_OFF_BODY"
                : "ML_FORECAST_ACTIVATION_ON_BODY",
            })}
          </Typography>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {intl.formatMessage({ id: "ML_FORECAST_ACTIVATION_NEXT_BATCH" })}
          </Alert>
          {mutation.isError && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {intl.formatMessage({ id: "ML_FORECAST_ACTIVATION_FAILED" })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" onClick={() => setOpen(false)}>
            {intl.formatMessage({ id: "CANCEL" })}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={mutation.isLoading}
            onClick={() => mutation.mutate(!active)}
          >
            {intl.formatMessage({
              id: active
                ? "ML_FORECAST_ACTIVATION_OFF_CONFIRM"
                : "ML_FORECAST_ACTIVATION_ON_CONFIRM",
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForecastActivation;
